import { useEffect, useState, type RefObject } from 'react';

/**
 * The element's current content width in pixels, or null before first measure.
 *
 * Needed because the flex column layout cannot be expressed in CSS. Percentage
 * `<col>` widths resolve against the table's own width, which under
 * `width: 100%` + `min-width` is circular — Chromium then resolves the
 * percentage against an indefinite basis, and a `calc()` mixing a percentage
 * with a pixel offset collapses to zero. Measuring the container and handing
 * the columns plain pixels sidesteps the whole problem.
 */
export const useContainerWidth = (ref: RefObject<HTMLElement | null>): number | null => {
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // jsdom has no ResizeObserver; fall back to a single read so tests still
    // get a definite width rather than staying null forever.
    if (typeof ResizeObserver === 'undefined') {
      setWidth(element.getBoundingClientRect().width || null);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      // Ignore the transient zero a detached or display:none ancestor reports;
      // committing it would collapse every column for a frame.
      if (measured > 0) setWidth(measured);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};
